/**
 * Setup steps can open in any order; completion comes from server artifacts. This composable owns the
 * active step; `useOnboardingRun` shares polling and side effects with linear onboarding.
 */
import { useFetch } from "~vue/api";
import type { OnboardingReading, OnboardingStep, RunState } from "~vue/CalendarFirstOnboarding/types";
import { useOnboardingRun } from "~vue/CalendarFirstOnboarding/useOnboardingRun";
import { type AskProgress, logAskEvent } from "~vue/ProfileSetup/setupAnalytics";
import type { ContextSnapshot, SetupExperimentArm, SetupState, SetupStep, SetupStepId } from "~vue/ProfileSetup/types";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { computed, type ComputedRef, type Ref, ref } from "vue";

const SETUP_STATE_URL = "/onboarding/calendar-first/setup/state/";
const SETUP_FINISH_URL = "/onboarding/calendar-first/setup/finish/";

/** Mirrors `PROFILE_SETUP_ENTRY` in `calendar_first/setup.py`. */
const PROFILE_SETUP_ENTRY = "profile_setup";

const CALENDAR_LAST_READINGS_DEADLINE_MS = 5 * 60_000;

export interface ProfileSetup {
    steps: Ref<SetupStep[]>;
    completedCount: Ref<number>;
    total: Ref<number>;
    context: ComputedRef<ContextSnapshot>;
    activeStep: Ref<SetupStepId | null>;
    isOpen: Ref<boolean>;
    run: ReturnType<typeof useOnboardingRun>;
    experimentArm: SetupExperimentArm | null;
    /** ENG-2135 treatment: Calendar is the closing ask and its analysis leads straight to the insights. */
    calendarLast: boolean;
    /** Whether the overview shows the readings and composer instead of the checklist. */
    insightsReady: ComputedRef<boolean>;
    /** Opens an ask, optionally at a later screen than its runner would choose; the runner reads it once on mount. */
    openStep: (id: SetupStepId, screen?: SetupScreen | null) => void;
    entryScreen: Ref<SetupScreen | null>;
    askProgress: (id: SetupStepId) => AskProgress;
    /** The ask that has to land before `id` can be taken, or null when nothing is in its way. */
    blockedBy: (id: SetupStepId) => SetupStepId | null;
    closeStep: () => Promise<void>;
    refreshState: () => Promise<void>;
    nextUnfinished: (excluding?: SetupStepId) => SetupStepId | null;
    finish: () => Promise<void>;
    /** Call on returning from calendar OAuth, so the one ask that completes off-page is still counted. */
    reportCalendarConnected: () => void;
    /** Seeded from page props and updated when polling returns nonempty readings. */
    readings: Ref<OnboardingReading[]>;
    readingsLoading: Ref<boolean>;
    loadReadings: () => void;
}

export function useProfileSetup(initial: SetupState): ProfileSetup {
    const steps = ref<SetupStep[]>(initial.steps);
    const completedCount = ref(initial.completed_count);
    const total = ref(initial.total);
    const activeStep = ref<SetupStepId | null>(null);
    const entryScreen = ref<SetupScreen | null>(null);
    const experimentArm = initial.experiment_arm;
    const calendarLast = experimentArm === "treatment";

    /* Calendar-last waits on the insights it ends with, which can take minutes, rather than giving up at the default. */
    const run = useOnboardingRun(initial.run, { entry: PROFILE_SETUP_ENTRY, readingsDeadlineMs: calendarLast ? CALENDAR_LAST_READINGS_DEADLINE_MS : undefined });

    /* The server scores context dimensions from the same artifacts used to determine setup completion. */
    const context = computed<ContextSnapshot>(() => run.run.value.context);

    const isOpen = computed(() => activeStep.value !== null);

    /*
     * Poll for readings produced during this session; page props supply earlier readings. Prefer sharpened
     * readings when perspective results exist.
     */
    let readingsRequested = false;
    const polling = ref(false);
    const sharpenedStarted = ref(false);

    /* A scan started later in the session can still be polled, so an early call without one is not the last. */
    const scanStarted = computed(() => run.run.value.scan_status === "syncing" || run.run.value.scan_status === "stats_ready");

    function loadReadings(): void {
        /*
         * Setup completion can come from artifacts predating this run. Without a calendar scan, no readings
         * can arrive; polling would waste its full 75-second deadline.
         */
        if (readingsRequested || !scanStarted.value) {
            return;
        }
        readingsRequested = true;

        const state = run.run.value;
        polling.value = true;
        if (state.has_perspective_result) {
            sharpenedStarted.value = true;
            run.sharpenedPhase.start();
        }
        run.readingsPhase.start();
    }

    /*
     * Page props cannot include scans completed later in this session. Accept nonempty polling results, but
     * preserve server-provided readings when a poll returns empty.
     */
    const polled = computed<OnboardingReading[]>(() => {
        const sharpened = run.sharpenedReadings.value?.readings ?? [];
        return sharpened.length > 0 ? sharpened : (run.scanReadings.value?.readings ?? []);
    });
    const seeded = ref<OnboardingReading[]>(initial.readings);
    const readings = computed<OnboardingReading[]>(() => (polled.value.length > 0 ? polled.value : seeded.value));

    /*
     * Unstarted phases never settle; waiting on them would keep the loading indicator active indefinitely.
     */
    const readingsSettled = computed(() => run.readingsSettled.value && (!sharpenedStarted.value || run.sharpenedSettled.value));
    const readingsLoading = computed(() => polling.value && readings.value.length === 0 && !readingsSettled.value);

    const { execute: executeStateFetch, data: stateData, error: stateError } = useFetch<SetupState>(SETUP_STATE_URL, { immediate: false }).get().json<SetupState>();

    /*
     * Completion is server-derived, so do not infer it locally. Preserve the last known checklist on fetch
     * failure to avoid clearing it.
     */
    async function refreshState(): Promise<void> {
        await executeStateFetch();
        if (stateError.value || stateData.value === null) {
            return;
        }
        const next = stateData.value;
        const previous = steps.value;
        steps.value = next.steps;
        completedCount.value = next.completed_count;
        total.value = next.total;
        run.run.value = next.run as RunState;
        seeded.value = next.readings;
        reportNewlyDone(previous);
    }

    function askProgress(id: SetupStepId): AskProgress {
        return {
            completedCount: completedCount.value,
            total: total.value,
            stepPosition: steps.value.findIndex((step) => step.id === id) + 1,
            experimentArm,
        };
    }

    /*
     * Every ask but the calendar finishes in this session, and the server is what decides it finished, so the
     * refresh that brings the new verdict back is the only honest place to read the transition. Watching a
     * runner instead would miss the ask a user completes and then immediately closes.
     */
    function reportNewlyDone(previous: SetupStep[]): void {
        const alreadyDone = new Set(previous.filter((step) => step.done).map((step) => step.id));
        for (const step of steps.value) {
            if (step.done && !alreadyDone.has(step.id)) {
                logAskEvent(AmplitudeEventNames.GUIDED_SETUP_ASK_COMPLETED, step.id, askProgress(step.id));
            }
        }
    }

    /**
     * Connecting a calendar leaves the page for the provider and comes back on a fresh load with the ask
     * already ticked, so there is no transition for `reportNewlyDone` to see. The OAuth return URL carries
     * the marker that brings us here.
     */
    function reportCalendarConnected(): void {
        if (isDone("calendar")) {
            logAskEvent(AmplitudeEventNames.GUIDED_SETUP_ASK_COMPLETED, "calendar", askProgress("calendar"));
        }
    }

    /**
     * Walks the server's step order, which the experiment arm decides. Avoid publish redirecting users back
     * to an assessment they just skipped. Declined asks stay excluded for this pass but remain available on
     * the profile page.
     */
    function nextUnfinished(excluding?: SetupStepId): SetupStepId | null {
        return steps.value.map((step) => step.id).find((id) => id !== excluding && !isDone(id) && !isDeclined(id) && resolveStep(id) !== excluding) ?? null;
    }

    function isDeclined(id: SetupStepId): boolean {
        if (id === "calendar") {
            return run.run.value.calendar_skipped;
        }
        if (id === "perspective") {
            return run.run.value.assessment_skipped;
        }
        return false;
    }

    function isDone(id: SetupStepId): boolean {
        return steps.value.find((step) => step.id === id)?.done === true;
    }

    /**
     * First publication needs a perspective report or every profile section is blank. Existing profiles
     * remain viewable without requiring another assessment. The calendar is not a prerequisite: the
     * generator is deliberately never shown it -- see `run_calendar_first_profile`.
     */
    function blockedBy(id: SetupStepId): SetupStepId | null {
        if (id === "publish" && !run.run.value.has_perspective_result && !isDone("publish")) {
            return "perspective";
        }
        return null;
    }

    /* Entry points that cannot show the block -- the home card, a context ring -- land on the ask that
       clears it rather than on a screen with nothing to draw. */
    function resolveStep(id: SetupStepId): SetupStepId {
        return blockedBy(id) ?? id;
    }

    function openStep(id: SetupStepId, screen: SetupScreen | null = null): void {
        const resolved = resolveStep(id);
        /* A screen belongs to the ask it was chosen for, not to the ask that clears its block. */
        entryScreen.value = resolved === id ? screen : null;
        activeStep.value = resolved;
    }

    /* Calendar-last shows insights once the calendar has been read, even with other asks still open. */
    const insightsReady = computed(() => steps.value.every((step) => step.done) || (calendarLast && isDone("calendar") && scanStarted.value));

    async function closeStep(): Promise<void> {
        run.stopAllPhases();
        /* A stopped poll never settles, so the overview restarts readings once it shows them again. */
        readingsRequested = false;
        activeStep.value = null;
        await refreshState();
    }

    const { execute: executeFinish } = useFetch(SETUP_FINISH_URL, { immediate: false }).post({}).json();

    async function finish(): Promise<void> {
        await executeFinish();
    }

    return {
        steps,
        completedCount,
        total,
        context,
        activeStep,
        isOpen,
        run,
        experimentArm,
        calendarLast,
        insightsReady,
        openStep,
        entryScreen,
        askProgress,
        blockedBy,
        closeStep,
        refreshState,
        nextUnfinished,
        finish,
        reportCalendarConnected,
        readings,
        readingsLoading,
        loadReadings,
    };
}

export type SetupScreen = OnboardingStep;

/**
 * Keep framing screens to explain each request, even for existing users. They render embedded and select
 * manager-specific copy using `manages_people`.
 */
export function screensFor(
    id: SetupStepId,
    { hasPerspectiveResult, calendarConnected, calendarLast = false }: { hasPerspectiveResult: boolean; calendarConnected: boolean; calendarLast?: boolean },
): SetupScreen[] {
    switch (id) {
        case "identity":
            return calendarLast ? ["identity"] : ["identity", "intro", "context_rings"];
        case "calendar":
            /* Calendar-last replaces the post-analysis screens with the insights on the overview. */
            if (calendarLast) {
                return ["calendar_connect", "scanning"];
            }
            /*
             * The first-pass payoff says the user's perspective is unavailable, so it would be misleading
             * when a perspective result already exists.
             */
            return hasPerspectiveResult ? ["calendar_connect", "scanning", "sharpened"] : ["calendar_connect", "scanning", "calendar_payoff"];
        case "perspective":
            /*
             * Users can take the assessment before connecting a calendar. Without a calendar, sharpened
             * readings are never dispatched, so that screen would wait indefinitely.
             */
            return calendarConnected && !calendarLast
                ? ["assessment_intro", "assessment", "perspective_result", "sharpened"]
                : ["assessment_intro", "assessment", "perspective_result"];
        case "publish":
            return ["profile_intro", "profile"];
    }
}
